import { useCallback, useEffect, useMemo } from 'react';

import SettingsTitle from '@views/admin/components/SettingsTitle';
import UnauthorizedView from '@views/auth/UnauthorizedView';
import { keyBy } from 'lodash';

import { AccountLink, TAG_ASSISTANT_LEARN_MORE_LINK } from '@sparkplug/lib';

import { LearnMoreDetails } from '@features/product-tags/views/AssigningProductTagsView/AssigningProductTagsView';

import Button from '@components/buttons/Button';
import Chip from '@components/chips/Chip';
import { AddCircleOutline as AddIcon, BookIcon } from '@components/icons';
import AppView from '@components/layout/AppView';
import RouterTabs from '@components/layout/Tabs/RouterTabs';
import Tooltip from '@components/layout/Tooltip';
import { useLocation, useRouteMatch } from '@components/router';
import Toolbar from '@components/toolbar/Toolbar';

import { useQueryParamsState } from '@hooks/QueryParamsHooks';
import { useSparkplugAccount } from '@hooks/SparkplugAccountsHooks';

import Intercom from '@helpers/Intercom';
import { applyFilterFns, openUrl } from '@helpers/ui';
import { getStateAbbreviationByName } from '@helpers/util';

import { IAccount } from '@app/types/AccountsTypes';
import { IOption } from '@app/types/UITypes';

import { useAccountLinks, useSuggestedAccountLinks } from '../../queries';
import AdminAccountLinksLinkedView from './LinkedAccountsTab';
import AdminAccountLinksSuggestedView from './SuggestedLinkTab';

import './AccountLinksView.scss';

const MarketsLabel = ({ markets = [] }: { markets?: string[] }) => {
    const marketsStr = markets.join(', ');

    if (markets.length > 5) {
        const slicedArr = markets.slice(0, 3);
        const remaining = markets.length - slicedArr.length;
        const shortString = `${slicedArr.join(', ')}, ...`;

        return (
            <Tooltip title={marketsStr}>
                <span>
                    {shortString}
                    <em>{`[+${remaining}]`}</em>
                </span>
            </Tooltip>
        );
    }

    return <>{marketsStr}</>;
};

const MAX_MARKETS_COUNT = 51; // 50 States + D.C.

export const useMarketOptionsFilter = ({ account }: { account?: IAccount }) => {
    const [query, setQuery] = useQueryParamsState<{
        markets: string[];
    }>({ markets: [] });
    const allAccountMarketOptions = useMemo<IOption[]>(
        () =>
            (account?.markets || []).map((label) => ({
                value: getStateAbbreviationByName(label),
                label,
            })),
        [account?.markets],
    );

    const setFilteredMarketOptions = (updatedFilterMarkets: IOption[]) => {
        const updatedQueryValue = updatedFilterMarkets.map(({ value }) => value);
        setQuery({ markets: updatedQueryValue });
    };

    const filteredMarketOptions = useMemo<IOption[]>(() => {
        if (query.markets.length === 0) {
            return allAccountMarketOptions;
        }

        const marketOptionsByValue = keyBy(allAccountMarketOptions, 'value');

        return query.markets.map((value: string) => marketOptionsByValue[value]);
    }, [allAccountMarketOptions, query.markets]);

    const applyMarketsFilter = useCallback(
        (accountLinks: AccountLink[]) => {
            if (!filteredMarketOptions.length) {
                return accountLinks;
            }

            const selectedMarketValues = filteredMarketOptions.map((option) => option.value);

            return accountLinks.filter((accountLink) =>
                accountLink.markets.some((accountMarketAbbr) =>
                    selectedMarketValues.includes(accountMarketAbbr),
                ),
            );
        },
        [filteredMarketOptions],
    );

    const filteredMarketsLabels = useMemo(() => {
        return filteredMarketOptions.map((option) => option.label);
    }, [filteredMarketOptions]);

    return {
        queryMarkets: query.markets,
        allAccountMarketOptions,
        setFilteredMarketOptions,
        filteredMarketOptions,
        filteredMarketsLabels,
        applyMarketsFilter,
    };
};

const AccountLinksView = () => {
    const { path } = useRouteMatch();
    const { pathname } = useLocation();
    const { account, connectEnabled } = useSparkplugAccount();
    const {
        queryMarkets,
        allAccountMarketOptions,
        setFilteredMarketOptions,
        filteredMarketOptions,
        applyMarketsFilter,
        filteredMarketsLabels,
    } = useMarketOptionsFilter({ account });

    const { accountLinksAreReady, accountLinks } = useAccountLinks(account?._id || '');
    const accountLinksFiltered = useMemo(
        () => applyFilterFns([applyMarketsFilter], accountLinks ?? []),
        [accountLinks, applyMarketsFilter],
    );

    const { suggestedAccountLinks, refetchSuggestedAccountLinks } = useSuggestedAccountLinks(
        account?._id,
    );

    const updateBrandLinkList = () => {
        refetchSuggestedAccountLinks();
    };

    useEffect(() => {
        updateBrandLinkList();
    }, [account]);

    const isSuggestedView = pathname.includes('suggested');
    const routes = useMemo(() => {
        const marketQueryString = queryMarkets
            .map((market: string) => `markets=${market}`)
            .join('&');

        return [
            {
                url: path,
                text: account?.type === 'retailer' ? 'Linked Vendors' : 'Linked Retailers',
                to: `${path}?${marketQueryString}`,
            },
            {
                url: `${path}/suggested`,
                text: (
                    <span className="suggested-brands-tablink">
                        <span>
                            {account?.type === 'retailer'
                                ? 'Suggested Vendors'
                                : 'Suggested Retailers'}
                        </span>
                        {accountLinksAreReady &&
                            !isSuggestedView &&
                            suggestedAccountLinks.length > 0 && (
                                <Chip
                                    color="dark-blue"
                                    label={suggestedAccountLinks.length.toString()}
                                />
                            )}
                    </span>
                ),
                to: `${path}/suggested?${marketQueryString}`,
            },
        ];
    }, [accountLinksAreReady, path, account, suggestedAccountLinks, queryMarkets]);

    const AddMarketsButton = () => (
        <Button
            className="add-markets-button"
            startIcon={<AddIcon />}
            onClick={() => Intercom.open()}
            variant="outlined"
        >
            Add Markets
        </Button>
    );

    const MarketsDropdown = () => (
        <Toolbar.DropdownListSelector
            label="Markets"
            options={allAccountMarketOptions}
            selected={filteredMarketOptions}
            onApply={(selected) => setFilteredMarketOptions(selected)}
            addOnComponent={
                allAccountMarketOptions.length < MAX_MARKETS_COUNT ? <AddMarketsButton /> : null
            }
        />
    );

    const toolbarMarketsComponent =
        allAccountMarketOptions.length > 1 ? (
            <MarketsDropdown />
        ) : (
            <MarketsLabel markets={account?.markets} />
        );

    const title = account?.type === 'retailer' ? 'Vendor Links' : 'Retailer Links';
    const tagAssistantLink =
        account?.type === 'brand' && !isSuggestedView ? (
            <Toolbar>
                <Toolbar.Button
                    className="learn-tagging"
                    startIcon={<BookIcon />}
                    onClick={() => openUrl(TAG_ASSISTANT_LEARN_MORE_LINK)}
                >
                    <LearnMoreDetails onlyAssistant />
                </Toolbar.Button>
            </Toolbar>
        ) : undefined;

    return (
        <AppView.Content
            title={<SettingsTitle label={title} />}
            toolbar={tagAssistantLink}
            className="admin-brandLinks-view"
        >
            {connectEnabled ? (
                <RouterTabs color="blue" tabs={routes}>
                    <RouterTabs.Tab path={`${path}/suggested`}>
                        <AdminAccountLinksSuggestedView
                            toolbarMarkets={toolbarMarketsComponent}
                            otherAccounts={suggestedAccountLinks}
                            filteredMarkets={filteredMarketsLabels}
                        />
                    </RouterTabs.Tab>
                    <RouterTabs.Tab path={path}>
                        <AdminAccountLinksLinkedView
                            accountLinksAreReady={accountLinksAreReady}
                            toolbarMarkets={toolbarMarketsComponent}
                            accountLinks={accountLinksFiltered}
                        />
                    </RouterTabs.Tab>
                </RouterTabs>
            ) : (
                <UnauthorizedView />
            )}
        </AppView.Content>
    );
};

export default AccountLinksView;
